import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Spin,
  Input,
  Select,
  Row,
  Col,
  Modal,
  Form,
  Popconfirm,
} from 'antd';
import {
  SafetyOutlined,
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { CompanyPermissionService, CompanyPermission, CompanyPermissionGrant } from '../../services/api/companyPermissionService';
import { PermissionService, Permission } from '../../services/api/permissionService';
import { useUser } from '../../contexts/UserContext';

const { Title, Text } = Typography;
const { Option } = Select;

interface CompanyPermissionManagerProps {
  companyId: string;
}

export const CompanyPermissionManager: React.FC<CompanyPermissionManagerProps> = ({ companyId }) => {
  const { user: currentUser } = useUser();
  const [companyPermissions, setCompanyPermissions] = useState<CompanyPermission[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [isGrantModalVisible, setIsGrantModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [permissions, allPermissions] = await Promise.all([
        CompanyPermissionService.getPermissions(companyId),
        PermissionService.getAll(),
      ]);
      setCompanyPermissions(permissions.filter((p) => p.is_active !== false));
      setAvailablePermissions(allPermissions);
    } catch (error: any) {
      message.error(error.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantPermission = async (values: {
    permission_name: string;
    resource_type: string;
    resource_id?: string;
  }) => {
    try {
      const grantData: CompanyPermissionGrant = {
        permission_name: values.permission_name,
        resource_type: values.resource_type,
        resource_id: values.resource_id || null,
        granted_by: currentUser?.id || '',
      };
      await CompanyPermissionService.grantPermission(companyId, grantData);
      message.success('Permission granted successfully');
      setIsGrantModalVisible(false);
      form.resetFields();
      await loadData();
    } catch (error: any) {
      message.error(error.message || 'Failed to grant permission');
    }
  };

  const handleRevokePermission = async (permissionName: string, resourceType: string) => {
    try {
      await CompanyPermissionService.revokePermission(companyId, {
        permission_name: permissionName,
        resource_type: resourceType,
      });
      message.success('Permission revoked successfully');
      await loadData();
    } catch (error: any) {
      message.error(error.message || 'Failed to revoke permission');
    }
  };

  // Get unique resource types
  const resourceTypes = Array.from(
    new Set(availablePermissions.map((p) => p.resource).filter(Boolean))
  );

  // Group permissions by resource type
  const groupedPermissions = companyPermissions.reduce(
    (acc, perm) => {
      const resource = perm.resource_type || 'other';
      if (!acc[resource]) {
        acc[resource] = [];
      }
      acc[resource].push(perm);
      return acc;
    },
    {} as Record<string, CompanyPermission[]>
  );

  const columns = [
    {
      title: 'Permission',
      dataIndex: 'permission_name',
      key: 'permission_name',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Resource Type',
      dataIndex: 'resource_type',
      key: 'resource_type',
      render: (text: string) => <Tag>{text}</Tag>,
    },
    {
      title: 'Resource ID',
      dataIndex: 'resource_id',
      key: 'resource_id',
      render: (id: string | null) => (id ? <Text code>{id}</Text> : <Text type="secondary">All</Text>),
    },
    {
      title: 'Granted At',
      dataIndex: 'granted_at',
      key: 'granted_at',
      render: (date: string) => (date ? new Date(date).toLocaleString() : '-'),
    },
    {
      title: 'Granted By',
      dataIndex: 'granted_by_username',
      key: 'granted_by_username',
      render: (username: string) => username || <Text type="secondary">Unknown</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: CompanyPermission) => (
        <Popconfirm
          title="Are you sure you want to revoke this permission?"
          onConfirm={() => handleRevokePermission(record.permission_name, record.resource_type)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="link" danger icon={<DeleteOutlined />} size="small">
            Revoke
          </Button>
        </Popconfirm>
      ),
    },
  ];

  if (loading && companyPermissions.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Card
        title={
          <Space>
            <SafetyOutlined />
            <Text strong>Company Permissions</Text>
            <Tag>{companyPermissions.length} Active</Tag>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsGrantModalVisible(true)}
          >
            Grant Permission
          </Button>
        }
      >
        {Object.keys(groupedPermissions).length > 0 ? (
          Object.keys(groupedPermissions).map((resource) => (
            <Card
              key={resource}
              title={
                <Space>
                  <Text strong>{resource.charAt(0).toUpperCase() + resource.slice(1)}</Text>
                  <Tag>{groupedPermissions[resource].length} permissions</Tag>
                </Space>
              }
              style={{ marginBottom: '16px' }}
              size="small"
            >
              <Table
                columns={columns}
                dataSource={groupedPermissions[resource]}
                rowKey={(record) => `${record.permission_name}-${record.resource_type}-${record.resource_id || 'all'}`}
                pagination={false}
                size="small"
              />
            </Card>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">No permissions granted to this company</Text>
          </div>
        )}
      </Card>

      <Modal
        title="Grant Company Permission"
        open={isGrantModalVisible}
        onCancel={() => {
          setIsGrantModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleGrantPermission}
        >
          <Form.Item
            name="permission_name"
            label="Permission"
            rules={[{ required: true, message: 'Please select a permission' }]}
          >
            <Select placeholder="Select permission" showSearch optionFilterProp="children">
              {availablePermissions.map((perm) => (
                <Option key={perm.id} value={perm.name}>
                  <Space>
                    <Text strong>{perm.name}</Text>
                    <Text type="secondary">- {perm.description}</Text>
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="resource_type"
            label="Resource Type"
            rules={[{ required: true, message: 'Please select a resource type' }]}
          >
            <Select placeholder="Select resource type">
              {resourceTypes.map((resource) => (
                <Option key={resource} value={resource}>
                  {resource.charAt(0).toUpperCase() + resource.slice(1)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="resource_id"
            label="Resource ID (Optional)"
            tooltip="Leave empty to grant permission to all resources of this type"
          >
            <Input placeholder="Specific resource ID or leave empty for all" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<CheckCircleOutlined />}>
                Grant Permission
              </Button>
              <Button onClick={() => {
                setIsGrantModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

